import i18n from '@i18next/i18n';
import { ActionService } from '.';
import { ApiErrorCode } from '@src/js/common/ark-extensions/dc-components/docs-component-size-limit-dialog/types';
import { MonitorStatus } from '@tencent/slide-sdk-model/src/manager/logger.interface';
import { openUrlForAll } from '@src/slide/infrastructure/module/independent/url/open-url';
import type { ICommonTipsConfig } from '@src/framework/frame/components/common-tips';
import type { ICreateDocResponse } from '@src/slide/domain/slide-app/sync/worker/work/service/request-service/doc-request-service';
import type { ILogger } from '../../../slide-data-services.core';
import type { INewDocData } from '@src/slide/domain/slide-app/sync/worker/work/create-doc';
import type { ICommitErrorInfo } from '@src/slide/domain/slide-app/sync/worker/work/job/commit-job/service/commit-service';
import type { IUploadResourceTaskOutputData } from '@src/slide/domain/slide-app/sync/worker/work/job/commit-job/task/resource-task/upload-task';
import type { IDocInfoManager } from '../../../interfaces/doc-info.interface';

export const oversizeRetCode = [20010, 20013];

export interface ILocalToOnlineProvider {
    showTips(config: ICommonTipsConfig): void
    hideTips(): void
    handleCreateSuccess(url: string): void
    changeReadyState?(state: boolean): void
    handleUpload50Progress?(): void;
    handleUpload100Progress?(): void;
}

export class LocalToOnlineMainActionService extends ActionService {
    protected loggerPrefix = '[LocalToOnlineMainActionService]';

    public constructor(
        protected logger: ILogger,
        protected docInfoManager: IDocInfoManager,
        protected provider: ILocalToOnlineProvider,
    ) {
        super(logger);
    }

    public async handleCreateSuccess(data: INewDocData): Promise<void> {
        this.logger.monitor?.reportStatus?.('LFT-create', MonitorStatus.SUCCESS, '', [this.docInfoManager.userId ?? '', data.docUrl]);
        this.logger.log(this.loggerPrefix, 'handleCreateSuccess', data);

        this.provider.showTips({ message: i18n.t('多人文档创建成功'), closable: true, autoClose: true });

        openUrlForAll({ url: data.docUrl });

        this.provider.handleCreateSuccess(data.docUrl);
    }

    public async handleCommitSuccess(commitTotal: number, lastCommitLength: number): Promise<void> {
        this.logger.monitor?.reportStatus?.('LFT-commit', MonitorStatus.SUCCESS);
        this.logger.log(this.loggerPrefix, 'handleCommitSuccess', commitTotal, lastCommitLength);

        const progress = Math.floor((commitTotal - lastCommitLength) / commitTotal * 100);
        this.provider.showTips({ message: i18n.t(`上传进度${progress}%`), closable: true });

        if (progress >= 50) {
            this.provider.handleUpload50Progress?.();
        }
    }

    public async handleCommitError(data: ICommitErrorInfo): Promise<void> {
        this.logger.monitor?.reportStatus?.('LFT-commit', MonitorStatus.ERROR, JSON.stringify(data), [this.docInfoManager.userId ?? '']);
        this.logger.error(this.loggerPrefix, 'handleCommitError', data);
        this.provider.showTips({ message: i18n.t('上传失败'), closable: true });
        this.provider.changeReadyState?.(false);
    }

    public async handleCreateError(data: Partial<ICreateDocResponse>): Promise<void> {
        this.logger.monitor?.reportStatus?.('LFT-create', MonitorStatus.ERROR, '', [this.docInfoManager.userId ?? '']);
        this.logger.log(this.loggerPrefix, 'handleCreateError', data);

        const retCode = data.retcode;
        const isOversize = retCode && this.handleRetCodeIsOversize(retCode);
        if (isOversize) return;

        this.provider.showTips({ message: i18n.t('创建失败，请稍候再试'), closable: true });
        this.provider.changeReadyState?.(false);
    }

    public async handleBeforeCommit(commitLength: number) {
        this.provider.showTips({ message: i18n.t(`准备中，预计上传${commitLength}个资源`), closable: true });
    }

    public async handleUploadError(data: IUploadResourceTaskOutputData): Promise<void> {
        this.logger.monitor?.reportStatus?.('LFT-upload', MonitorStatus.ERROR, JSON.stringify(data), [this.docInfoManager.userId ?? '']);
        this.logger.error(this.loggerPrefix, 'handleUploadError', data);

        this.provider.changeReadyState?.(false);

        const retCode = data.reason?.code;
        const isOversize = retCode && await this.handleRetCodeIsOversize(retCode);
        if (isOversize) return;

        this.provider.showTips({ message: i18n.t('上传资源失败，请手动重新上传'), closable: true });
    }

    public async handleCommitComplete(): Promise<void> {
        this.logger.monitor?.reportStatus?.('LFT-commit', MonitorStatus.SUCCESS);
        this.logger.monitor?.reportStatus?.('LFT-complete', MonitorStatus.SUCCESS);
        this.provider.hideTips();

        this.provider.handleUpload100Progress?.();
    }

    public async handleSharedWorkerConnected() {
        this.provider.changeReadyState?.(true);
    }

    protected async handleRetCodeIsOversize(retCode: number) {
        if (oversizeRetCode.includes(retCode)) {
            const code = retCode as ApiErrorCode;
            this.provider.showTips({ message: i18n.t('资源体积超过上限，请开通会员'), closable: true });
            const { convertToMultiDocShowDialog } = await import('@src/js/common/ark-extensions/dc-components/docs-component-size-limit-dialog');
            convertToMultiDocShowDialog(code);
            return true;
        }
        return false;
    }
}
